import { useEffect } from "react";
import { useAuth, useStoredAuth } from "#/hooks/use-auth";
import { useMe } from "#/hooks/use-me";
import { clearStoredAuth, isAuthValid } from "#/lib/api/token-storage";

// Re-check expiry on this cadence so a tab left open flips to logged-out
// close to when the token actually dies (tokens live 3 days; the 5-min
// storage skew already fronts the real expiry).
const EXPIRY_SWEEP_INTERVAL_MS = 60_000;

/**
 * Session lifecycle. Sessions are created by the login dialog (email code →
 * `/v1/auth/email/verify`) and can't be renewed silently (no refresh tokens),
 * so all that's left to sync is expiry: drop a stored session once it ages
 * out, which flips `useAuth()`/`useWallet()` app-wide via the storage event.
 */
export function AuthSync() {
	const stored = useStoredAuth();
	const { isAuthenticated } = useAuth();

	useEffect(() => {
		if (!stored) return;

		function sweep() {
			if (!isAuthValid(stored)) clearStoredAuth();
		}

		sweep();
		const timer = setInterval(sweep, EXPIRY_SWEEP_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [stored]);

	return import.meta.env.DEV ? (
		<MeDevLogger isAuthenticated={isAuthenticated} />
	) : null;
}

function MeDevLogger({ isAuthenticated }: { isAuthenticated: boolean }) {
	const me = useMe();
	useEffect(() => {
		if (!isAuthenticated) return;
		if (me.data) {
			console.info("[auth] /auth/me", me.data);
		} else if (me.error) {
			console.error("[auth] /auth/me error", me.error);
		}
	}, [isAuthenticated, me.data, me.error]);
	return null;
}
